import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, copyText } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type DisplayCollectionDTO } from "../../api";
import { formatDate } from "../../components/cells";
import { crudEditor, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { hashPassword } from "../../lib/password";
import { LogicView } from "../../views";

/** Where the public gallery is served from, for the copy-URL cell. */
const PREVIEW_BASE = "https://admin.clicart.com/preview/";

/**
 * Display collections — password-gated galleries shared with a buyer.
 *
 * The password column never shows a value: the row carries a BCrypt hash, and
 * typing a new one replaces it. Leaving the field blank keeps the current
 * password, which is why the editor writes only on a non-empty value.
 */
export function DisplayCollectionsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.COLLECTION_DISPLAY);
  const title = t("navigation.item.display-collections", "Display");

  const { data, isLoading, error } = useQuery({
    queryKey: ["display-collection"],
    queryFn: () => api.displayCollections.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["display-collection"] });

  const cols = useMemo(() => {
    const c = defineColumns<DisplayCollectionDTO>();
    return [
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.custom("password", () => "", () => "*******", {
        headerKey: "column.password",
        filter: "none",
        enableSorting: false,
        priority: 5,
      }),
      c.text("created_at", (row) => formatDate(row.created_at), {
        headerKey: "column.creation-date",
        filter: "daterange",
        priority: 3,
      }),
      c.custom(
        "url",
        (row) => row.uuid ?? "",
        (row) => (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              void copyText(
                `${PREVIEW_BASE}${row.uuid}`,
                t("notification.copy-clipboard.success", "Copied to clipboard"),
                t("notification.copy-clipboard.error", "Unable to copy to clipboard"),
              );
            }}
          >
            {t("value.copy-url", "Copy URL")}
          </button>
        ),
        { headerKey: "column.url", filter: "none", enableSorting: false, priority: 4 },
      ),
      c.number("image_count", (row) => row.images?.length ?? 0, {
        headerKey: "column.image-count",
        priority: 2,
      }),
    ];
  }, [t]);

  const editor = useMemo(
    () =>
      crudEditor<DisplayCollectionDTO>({
        client: api.displayCollections,
        refresh,
        t,
        fields: {
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
          // The draft holds the plaintext; onSave hashes it before writing.
          // The getter has to read the draft back or the cell cannot be typed
          // into — `toDraft` below is what keeps the stored hash off screen.
          password: {
            kind: "text",
            get: (row) => row.password ?? "",
            set: (row, value) => ({ ...row, password: value }),
            placeholder: t("form.field.password.keep", "Leave blank to keep"),
          },
        },
        newRow: () => ({ id: 0, name: "", password: "", images: [] }),
        // The row arrives carrying a BCrypt hash. Blanking it once, as editing
        // starts, is what lets the cell behave like an ordinary text field:
        // empty means "unchanged", anything typed becomes the new password.
        toDraft: (row) => ({ ...row, password: "" }),
        validate: (row, isNew) => {
          const errors: Record<string, string> = {};
          if (!row.name.trim()) errors.name = t("form.validation.field.required", "Value is required");
          else if (rows.some((entry) => entry.id !== row.id && entry.name === row.name)) {
            errors.name = t("form.validation.field.unique", "Value already exists");
          }
          const typed = row.password ?? "";
          // A new collection needs a password; an edit may leave it untouched.
          if ((isNew || typed.length > 0) && typed.length < 6) {
            errors.password = t(
              "form.validation.field.length",
              "Value must be at least {{min}} characters",
              { min: 6 },
            );
          }
          return Object.keys(errors).length > 0 ? errors : null;
        },
        onSave: async (row, isNew) => {
          const typed = row.password ?? "";
          // `previous.password` is the API's own value, already in wire form;
          // `hashPassword` returns wire form too. Nothing here handles a bare hash.
          const previous = rows.find((entry) => entry.id === row.id);
          const payload: DisplayCollectionDTO = {
            ...row,
            password: typed.length > 0 ? await hashPassword(typed) : (previous?.password ?? ""),
          };
          if (isNew) await api.displayCollections.create(payload);
          else await api.displayCollections.update(payload);
        },
      }),
    [api, rows, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<DisplayCollectionDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/collection/display-collections"
        loading={isLoading}
        editor={editor}
        onRowClick={(row) =>
          void navigate({
            to: "/collection/display-collections/$id",
            params: { id: String(row.id) },
          })
        }
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("tab.images", "Images")}
            icon={<Eye className="size-3.5" />}
            onClick={() =>
              void navigate({
                to: "/collection/display-collections/$id",
                params: { id: String(row.id) },
              })
            }
          />
        )}
      />
    </>
  );
}
